import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Eye, EyeOff } from 'lucide-react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { customerAuthService } from '../../services/customer-auth.service';
import { teboxDesignSystem, useBookingTebox } from '../../styles/tebox-booking';
import { getApiBaseUrl } from '../../services/api';
import {
  buildBookingOAuthState,
  generateOAuthNonce,
  parseBookingOAuthState,
} from '../../utils/booking-oauth-state';
import {
  BOOKING_PARTNER_INVITE_OAUTH_RETURN_KEY,
  BOOKING_PARTNER_INVITE_PENDING_CLAIM_KEY,
  BOOKING_SLUG_PATTERN,
  isSafePartnerInviteReturnPath,
  stripOAuthReturnSearchParams,
} from '../../utils/club-route-key';
import {
  getGoogleOAuthBlockedReason,
  getGoogleOAuthRedirectUri,
} from '../../utils/google-oauth-redirect';
import { formatMoney } from '../../utils/format-money';
import {
  buildBookingLoginModalStyles,
  resolveBookingBrandColors,
  type BookingBrandColors,
} from '../../utils/booking-brand-colors';

interface LoginRegisterModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (customer: any, auth?: { token: string }) => void;
  teeTimeInfo?: {
    dateTime: string;
    courseName: string;
    price: number;
  };
  selectedCustomerTypeId?: string; // Customer type selected from rate selector
  brandColors?: BookingBrandColors;
  clubId?: string; // Club ID for OAuth multi-tenancy
  clubNumber?: string | number; // Club number as alternative
  /** Public booking URL slug (stable identifier). */
  bookingSlug?: string;
  clubName?: string; // Club name/slug from URL (online booking)
}

interface LoginForm {
  email: string;
  password: string;
}

interface RegisterForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

const LoginRegisterModal: React.FC<LoginRegisterModalProps> = ({
  open,
  onClose,
  onSuccess,
  teeTimeInfo,
  selectedCustomerTypeId,
  clubId,
  clubNumber,
  bookingSlug,
  clubName,
  brandColors: brandColorsProp,
}) => {
  useBookingTebox();
  const brand = brandColorsProp ?? resolveBookingBrandColors(null);
  const modal = React.useMemo(() => buildBookingLoginModalStyles(brand), [brand]);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);
  const oauthExchangeStartedRef = React.useRef(false);

  // Login form state
  const [loginForm, setLoginForm] = useState<LoginForm>({
    email: '',
    password: ''
  });

  // Register form state
  const [registerForm, setRegisterForm] = useState<RegisterForm>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const setTab = (index: number) => {
    setActiveTab(index);
    setMessage('');
    setError('');
    setShowLoginPassword(false);
    setShowRegisterPassword(false);
    setShowRegisterConfirmPassword(false);
  };

  const handleLogin = async () => {
    if (!loginForm.email || !loginForm.password) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await customerAuthService.login(loginForm.email, loginForm.password, {
        clubId,
        clubNumber,
        bookingSlug,
        clubName
      });
      
      setMessage('Login successful!');
      setTimeout(() => {
        onSuccess(response.customer, { token: response.token });
        onClose();
      }, 1000);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    // Validation
    if (!registerForm.firstName || !registerForm.lastName || !registerForm.email || !registerForm.phone) {
      setError('Please fill in all required fields');
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (registerForm.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(registerForm.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Phone validation (basic)
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (!phoneRegex.test(registerForm.phone.replace(/[\s\-\(\)]/g, ''))) {
      setError('Please enter a valid phone number');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await customerAuthService.register({
        firstName: registerForm.firstName,
        lastName: registerForm.lastName,
        email: registerForm.email,
        phone: registerForm.phone,
        password: registerForm.password,
        customerTypeId: selectedCustomerTypeId, // Include selected customer type from rate selector
        clubId,
        clubNumber,
        bookingSlug,
        clubName
      });
      
      setMessage('Account created successfully!');
      setTimeout(() => {
        onSuccess(response.customer, { token: response.token });
        onClose();
      }, 1000);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError('');

      // Nonce for CSRF; full OAuth state embeds club context (Safari mobile often loses sessionStorage).
      const nonce = generateOAuthNonce();
      sessionStorage.setItem('oauth_state', nonce);
      sessionStorage.setItem('oauth_provider', 'google');
      if (clubId) {
        sessionStorage.setItem('oauth_club_id', clubId);
      }
      if (clubNumber) {
        sessionStorage.setItem('oauth_club_number', String(clubNumber));
      }
      if (bookingSlug?.trim()) {
        sessionStorage.setItem('oauth_booking_slug', bookingSlug.trim().toLowerCase());
      }
      if (clubName?.trim()) {
        sessionStorage.setItem('oauth_club_name', clubName.trim());
      }
      if (selectedCustomerTypeId) {
        sessionStorage.setItem('oauth_customer_type_id', selectedCustomerTypeId);
      }

      const joinPath = location.pathname;
      if (isSafePartnerInviteReturnPath(joinPath)) {
        sessionStorage.setItem(BOOKING_PARTNER_INVITE_OAUTH_RETURN_KEY, joinPath);
        const rawSeg = joinPath.slice('/join/'.length).split('/')[0] ?? '';
        if (rawSeg) {
          try {
            sessionStorage.setItem(BOOKING_PARTNER_INVITE_PENDING_CLAIM_KEY, decodeURIComponent(rawSeg));
          } catch {
            sessionStorage.setItem(BOOKING_PARTNER_INVITE_PENDING_CLAIM_KEY, rawSeg);
          }
        }
      }

      // Get OAuth configuration from environment
      const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!googleClientId) {
        setError('Google OAuth is not configured. Please contact support.');
        setLoading(false);
        return;
      }

      const oauthBlocked = getGoogleOAuthBlockedReason();
      if (oauthBlocked) {
        setError(oauthBlocked);
        setLoading(false);
        return;
      }

      // Must match API GOOGLE_REDIRECT_URI and Google Cloud "Authorized redirect URIs" exactly
      const redirectUri = getGoogleOAuthRedirectUri();

      const oauthState = buildBookingOAuthState({
        nonce,
        clubNumber: clubNumber != null ? String(clubNumber) : undefined,
        clubId: clubId?.trim() || undefined,
        bookingSlug: bookingSlug?.trim().toLowerCase() || undefined,
        clubName: clubName?.trim() || undefined,
        customerTypeId: selectedCustomerTypeId || undefined,
      });
      
      // Build Google OAuth URL
      const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${encodeURIComponent(googleClientId)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `response_type=code&` +
        `scope=email profile&` +
        `state=${encodeURIComponent(oauthState)}&` +
        `access_type=offline&` +
        `prompt=consent`;

      // Redirect to Google
      window.location.href = googleAuthUrl;
    } catch (error) {
      console.error('Google OAuth error:', error);
      setError('Failed to initiate Google sign-in. Please try again.');
      setLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    try {
      setLoading(true);
      setError('');

      const nonce = generateOAuthNonce();
      sessionStorage.setItem('oauth_state', nonce);
      sessionStorage.setItem('oauth_provider', 'apple');
      if (clubId) {
        sessionStorage.setItem('oauth_club_id', clubId);
      }
      if (clubNumber) {
        sessionStorage.setItem('oauth_club_number', String(clubNumber));
      }
      if (bookingSlug?.trim()) {
        sessionStorage.setItem('oauth_booking_slug', bookingSlug.trim().toLowerCase());
      }
      if (clubName?.trim()) {
        sessionStorage.setItem('oauth_club_name', clubName.trim());
      }
      if (selectedCustomerTypeId) {
        sessionStorage.setItem('oauth_customer_type_id', selectedCustomerTypeId);
      }

      // Get OAuth configuration from environment
      const appleClientId = import.meta.env.VITE_APPLE_CLIENT_ID;
      if (!appleClientId) {
        setError('Apple Sign-In is not configured. Please contact support.');
        setLoading(false);
        return;
      }

      // Build redirect URI - redirect back to same page with OAuth params
      const redirectUri = `${window.location.origin}${window.location.pathname}`;

      // Build Apple OAuth URL
      // Note: Use query mode (default) - form_post requires a callback page
      const oauthState = buildBookingOAuthState({
        nonce,
        clubNumber: clubNumber != null ? String(clubNumber) : undefined,
        clubId: clubId?.trim() || undefined,
        bookingSlug: bookingSlug?.trim().toLowerCase() || undefined,
        clubName: clubName?.trim() || undefined,
        customerTypeId: selectedCustomerTypeId || undefined,
      });

      const appleAuthUrl = `https://appleid.apple.com/auth/authorize?` +
        `client_id=${encodeURIComponent(appleClientId)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `response_type=code id_token&` +
        `scope=email name&` +
        `state=${encodeURIComponent(oauthState)}&` +
        `response_mode=query`;

      // Redirect to Apple
      window.location.href = appleAuthUrl;
    } catch (error) {
      console.error('Apple OAuth error:', error);
      setError('Failed to initiate Apple sign-in. Please try again.');
      setLoading(false);
    }
  };

  const resetForms = () => {
    setLoginForm({ email: '', password: '' });
    setRegisterForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: ''
    });
    setMessage('');
    setError('');
  };

  const handleClose = () => {
    resetForms();
    onClose();
  };

  // Listen for custom events to set the active tab
  React.useEffect(() => {
    const handleSetTab = (event: CustomEvent) => {
      const { tab } = event.detail;
      setActiveTab(tab);
    };

    window.addEventListener('setLoginModalTab', handleSetTab as EventListener);
    return () => {
      window.removeEventListener('setLoginModalTab', handleSetTab as EventListener);
    };
  }, []);

  // Exchange OAuth code as soon as it appears in the URL — do not wait for `open`.
  // Mobile Safari often clears sessionStorage on the Google redirect; club context lives in `state`.
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const id_token = params.get('id_token');

    if (!(code || id_token) || !state) return;
    if (oauthExchangeStartedRef.current) return;
    oauthExchangeStartedRef.current = true;

    console.log('🔍 LoginRegisterModal: OAuth return detected, exchanging code');
    void handleOAuthCallback(code, id_token, state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleOAuthCallback = async (code: string | null, id_token: string | null, state: string | null) => {
    const pathnameWhenReturning = location.pathname;
    const searchWhenReturning = location.search;

    try {
      setLoading(true);
      setError('');

      console.log('🔄 handleOAuthCallback called with:', { code: !!code, id_token: !!id_token, state: !!state });

      const storedNonce = sessionStorage.getItem('oauth_state');
      const parsedState = state ? parseBookingOAuthState(state) : null;
      if (parsedState?.nonce) {
        if (storedNonce && parsedState.nonce !== storedNonce) {
          console.error('❌ OAuth state mismatch (nonce):', { storedNonce, receivedNonce: parsedState.nonce });
          throw new Error('Invalid OAuth state. Please try again.');
        }
      } else if (storedNonce && state && state !== storedNonce) {
        // Legacy: state was the bare nonce UUID
        console.error('❌ OAuth state mismatch:', { storedNonce, receivedState: state });
        throw new Error('Invalid OAuth state. Please try again.');
      }

      // Get provider from sessionStorage, or default to 'google' if we have a code
      let storedProvider = sessionStorage.getItem('oauth_provider');
      if (!storedProvider && code) {
        console.log('⚠️ No provider in sessionStorage, defaulting to Google');
        storedProvider = 'google';
      } else if (!storedProvider) {
        console.error('❌ No OAuth provider found and no code available');
        throw new Error('OAuth provider not found. Please try again.');
      }

      // Get stored context
      const storedClubId = sessionStorage.getItem('oauth_club_id');
      const storedClubNumber = sessionStorage.getItem('oauth_club_number');
      const storedBookingSlug = sessionStorage.getItem('oauth_booking_slug');
      const storedClubName = sessionStorage.getItem('oauth_club_name');
      const storedCustomerTypeId = sessionStorage.getItem('oauth_customer_type_id');

      // Clean up session storage
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_provider');
      sessionStorage.removeItem('oauth_club_id');
      sessionStorage.removeItem('oauth_club_number');
      sessionStorage.removeItem('oauth_booking_slug');
      sessionStorage.removeItem('oauth_club_name');
      sessionStorage.removeItem('oauth_customer_type_id');

      const apiBaseUrl = getApiBaseUrl();
      const endpoint = `${apiBaseUrl}/customer-auth/oauth/${storedProvider}`;

      // Prepare request body
      const urlClub = new URLSearchParams(searchWhenReturning);
      const requestBody: any = {
        bookingSlug:
          storedBookingSlug ||
          parsedState?.bookingSlug ||
          (bookingSlug?.trim() ? bookingSlug.trim().toLowerCase() : undefined) ||
          urlClub.get('bookingSlug') ||
          undefined,
        clubId:
          storedClubId ||
          parsedState?.clubId ||
          clubId ||
          urlClub.get('clubId') ||
          undefined,
        clubNumber:
          storedClubNumber ||
          parsedState?.clubNumber ||
          clubNumber ||
          urlClub.get('clubNumber') ||
          undefined,
        clubName:
          storedClubName ||
          parsedState?.clubName ||
          clubName?.trim() ||
          urlClub.get('clubName') ||
          undefined,
        customerTypeId:
          storedCustomerTypeId ||
          parsedState?.customerTypeId ||
          selectedCustomerTypeId ||
          undefined,
      };

      if (storedProvider === 'google' && code) {
        requestBody.code = code;
        requestBody.redirectUri = getGoogleOAuthRedirectUri();
      } else if (storedProvider === 'apple') {
        if (id_token) {
          requestBody.id_token = id_token;
        } else if (code) {
          requestBody.code = code;
        } else {
          throw new Error('No authorization code or ID token received');
        }
      }

      // Call backend OAuth endpoint
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        let msg = typeof data.error === 'string' ? data.error : 'OAuth authentication failed';
        if (data.details && typeof data.details === 'string' && !msg.includes(data.details.slice(0, 80))) {
          try {
            const d = JSON.parse(data.details) as { error_description?: string };
            if (d.error_description && !msg.includes(d.error_description)) {
              msg = `${msg} (${d.error_description})`;
            }
          } catch {
            if (data.details.length < 200) msg = `${msg} — ${data.details}`;
          }
        }
        throw new Error(msg);
      }

      if (data.success && data.data) {
        // Store token and customer using the auth service format
        const expiresIn = data.data.expiresIn || (30 * 24 * 60 * 60); // 30 days in seconds
        const session = {
          token: data.data.token,
          customer: {
            id: data.data.customer.id,
            email: data.data.customer.email,
            firstName: data.data.customer.firstName,
            lastName: data.data.customer.lastName,
            phone: data.data.customer.phone,
            customerType: data.data.customer.customerType as 'member' | 'guest',
            customerTypeId: data.data.customer.customerTypeId
          },
          expiresAt: Date.now() + (expiresIn * 1000) // Convert to milliseconds
        };
        
        // Store in both formats for compatibility
        localStorage.setItem('booking_service_auth', JSON.stringify(session));
        localStorage.setItem('authToken', data.data.token);
        localStorage.setItem('customer', JSON.stringify(data.data.customer));
        
        console.log('✅ OAuth authentication successful, session stored');

        setMessage('Sign-in successful!');
        
        const slugRestore =
          storedBookingSlug ||
          parsedState?.bookingSlug ||
          (bookingSlug?.trim() ? bookingSlug.trim().toLowerCase() : undefined) ||
          urlClub.get('bookingSlug');
        const slugLower = slugRestore ? String(slugRestore).toLowerCase() : '';

        const stayOnPartnerJoinPath =
          pathnameWhenReturning.startsWith('/join/') || pathnameWhenReturning === '/join';

        const partnerReturnStored = sessionStorage.getItem(BOOKING_PARTNER_INVITE_OAUTH_RETURN_KEY);
        const partnerReturnPath =
          partnerReturnStored && isSafePartnerInviteReturnPath(partnerReturnStored)
            ? partnerReturnStored
            : null;

        let shouldNotifyParentOAuthSuccess = true;

        if (partnerReturnPath) {
          sessionStorage.removeItem(BOOKING_PARTNER_INVITE_OAUTH_RETURN_KEY);
          navigate({ pathname: partnerReturnPath, search: '' }, { replace: true });
          shouldNotifyParentOAuthSuccess = false;
        } else if (stayOnPartnerJoinPath) {
          const params = new URLSearchParams(searchWhenReturning);
          stripOAuthReturnSearchParams(params);
          const q = params.toString();
          navigate({ pathname: pathnameWhenReturning, search: q ? `?${q}` : '' }, { replace: true });
        } else if (slugLower && BOOKING_SLUG_PATTERN.test(slugLower)) {
          navigate(`/${slugLower}`, { replace: true });
        } else {
          const sp = new URLSearchParams();
          if (slugRestore) sp.set('bookingSlug', String(slugRestore).toLowerCase());
          else if (storedClubId) sp.set('clubId', storedClubId);
          else if (storedClubNumber) sp.set('clubNumber', storedClubNumber);
          else if (storedClubName) sp.set('clubName', storedClubName);
          const q = sp.toString();
          navigate({ pathname: '/', search: q ? `?${q}` : '' }, { replace: true });
        }

        if (shouldNotifyParentOAuthSuccess) {
          setTimeout(() => {
            onSuccess(session.customer, { token: session.token });
            onClose();
          }, 1000);
        }
      } else {
        const msg =
          typeof data?.error === 'string'
            ? data.error
            : 'OAuth completed but the server response was unexpected. Check the Network tab for POST /customer-auth/oauth/google.';
        console.error('OAuth unexpected response shape:', data);
        throw new Error(msg);
      }
    } catch (error) {
      console.error('OAuth callback error:', error);
      setError(error instanceof Error ? error.message : 'OAuth authentication failed');
      const params = new URLSearchParams(searchWhenReturning);
      stripOAuthReturnSearchParams(params);
      const q = params.toString();
      navigate({ pathname: pathnameWhenReturning, search: q ? `?${q}` : '' }, { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const modalTitle =
    activeTab === 1
      ? 'Create your account'
      : teeTimeInfo
        ? 'Finish your booking'
        : 'Welcome back';
  const modalSubtitle =
    activeTab === 1
      ? teeTimeInfo
        ? 'Add your details to complete this reservation.'
        : 'Sign up to save your info for next time.'
      : teeTimeInfo
        ? 'Log in or create an account to confirm your tee time.'
        : 'Log in to continue with your booking.';

  /** Matches other booking modals — club border radius + brand chrome. */
  const cardRadius = modal.cardRadius;
  const innerRadius = modal.innerRadius;
  const hairline = modal.hairline;
  const accent = modal.accent;
  const modalFont = modal.fontFamily;
  const modalInk = modal.ink;

  /** 8px grid: one scale for the whole modal */
  const padX = { xs: 3, sm: 4 };
  /** Header — slightly tighter than body so title block doesn’t feel overly airy */
  const padYTop = { xs: 3, sm: 3.5 };
  const padYBottom = { xs: 3, sm: 4 };
  /** Matches `PricingModal` inner column gap (24px) for section rhythm. */
  const gapSection = 3;
  const gapFields = 2;
  const gapLabelInput = 0.5;

  const fieldLabelSx = {
    display: 'block' as const,
    fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
    fontWeight: 500,
    lineHeight: 1.3,
    color: modalInk.body,
    fontFamily: modalFont,
    mb: gapLabelInput,
  };

  const inputSx = {
    fontFamily: modalFont,
    '& .MuiInputBase-input': {
      fontSize: teboxDesignSystem.typography.body.fontSize,
      color: modalInk.body,
      fontFamily: modalFont,
      py: 1.25,
      '&::placeholder': {
        color: modalInk.muted,
        opacity: 0.85,
      },
    },
    '& .MuiOutlinedInput-root': {
      fontFamily: modalFont,
      borderRadius: innerRadius,
      backgroundColor: modal.panelBg,
      transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: hairline },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: modal.socialButton.hoverBorderColor,
      },
      '&.Mui-focused': {
        boxShadow: `0 0 0 2px ${accent}28`,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: accent,
        borderWidth: '1px',
      },
    },
  };

  const primaryCtaSx = {
    borderRadius: innerRadius,
    backgroundColor: accent,
    color: modal.primaryText,
    textTransform: 'none' as const,
    fontSize: teboxDesignSystem.typography.body.fontSize,
    fontWeight: 500,
    fontFamily: modalFont,
    py: 1.25,
    letterSpacing: '-0.01em',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: accent,
      filter: 'brightness(1.05)',
      boxShadow: `0 10px 28px -8px ${accent}66`,
    },
    '&:disabled': {
      backgroundColor: brand.secondaryColor,
      color: modal.primaryText,
      opacity: 0.7,
    },
  };

  const passwordAdornment = (visible: boolean, onToggle: () => void) => (
    <InputAdornment position="end">
      <IconButton
        type="button"
        aria-label={visible ? 'Hide password' : 'Show password'}
        onClick={onToggle}
        edge="end"
        size="small"
        sx={{ color: modalInk.muted }}
      >
        {visible ? <EyeOff size={18} strokeWidth={2} aria-hidden /> : <Eye size={18} strokeWidth={2} aria-hidden />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth={false}
      scroll="paper"
      disableScrollLock
      aria-labelledby="login-register-modal-title"
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        },
      }}
      PaperProps={{
        sx: {
          width: 'min(500px, calc(100vw - 32px))',
          maxWidth: '100%',
          borderRadius: cardRadius,
          border: `1px solid ${hairline}`,
          boxShadow: 'none',
          fontFamily: modalFont,
          overflow: 'hidden',
          bgcolor: modal.panelBg,
        },
      }}
    >
      <DialogTitle component="div" sx={{ m: 0, p: 0, bgcolor: modal.panelBg }}>
        <Box
          sx={{
            px: padX,
            pt: padYTop,
            pb: teeTimeInfo ? 2 : 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                pr: 0.5,
                alignSelf: 'flex-start',
              }}
            >
              {brand.logoUrl ? (
                <Box
                  component="img"
                  src={brand.logoUrl}
                  alt=""
                  sx={{
                    display: 'block',
                    height: 32,
                    maxWidth: 140,
                    objectFit: 'contain',
                    mb: 1,
                  }}
                />
              ) : null}
              <Typography
                component="h2"
                id="login-register-modal-title"
                sx={{
                  fontSize: teboxDesignSystem.typography.h3.fontSize,
                  fontWeight: teboxDesignSystem.typography.h3.fontWeight,
                  letterSpacing: teboxDesignSystem.typography.h3.letterSpacing,
                  lineHeight: teboxDesignSystem.typography.h3.lineHeight,
                  color: modalInk.title,
                  fontFamily: modalFont,
                  mb: 0.5,
                }}
              >
                {modalTitle}
              </Typography>
              <Typography
                component="p"
                sx={{
                  m: 0,
                  fontSize: teboxDesignSystem.typography.body.fontSize,
                  fontWeight: 400,
                  lineHeight: 1.5,
                  color: modalInk.muted,
                  fontFamily: modalFont,
                  maxWidth: 400,
                }}
              >
                {modalSubtitle}
              </Typography>
            </Box>
            <IconButton
              aria-label="Close dialog"
              onClick={handleClose}
              size="small"
              sx={{
                flexShrink: 0,
                mt: -0.25,
                mr: 0,
                color: modalInk.muted,
                bgcolor: modal.cardChrome.backgroundColor,
                borderRadius: innerRadius,
                border: modal.cardChrome.border,
                '&:hover': {
                  bgcolor: modal.cardChrome.backgroundColor,
                  filter: 'brightness(0.98)',
                  color: modalInk.body,
                },
              }}
            >
              <X size={18} strokeWidth={2.25} aria-hidden />
            </IconButton>
          </Box>

          {teeTimeInfo ? (
            <Box
              sx={{
                mt: 2,
                px: 2,
                py: 1.25,
                ...modal.cardChrome,
                borderRadius: cardRadius,
                fontFamily: modalFont,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  fontSize: teboxDesignSystem.typography.caption.fontSize,
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                  lineHeight: 1.2,
                  color: modalInk.muted,
                  fontFamily: modalFont,
                  mb: 0.375,
                  textTransform: 'uppercase',
                }}
              >
                Your tee time
              </Typography>
              <Typography
                component="p"
                sx={{
                  m: 0,
                  fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                  fontWeight: 500,
                  lineHeight: 1.35,
                  color: modalInk.body,
                  fontFamily: modalFont,
                  mb: 0.25,
                }}
              >
                {(() => {
                  const d = new Date(teeTimeInfo.dateTime);
                  const dateShort = d.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  });
                  const timeShort = d.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                  });
                  return `${dateShort} · ${timeShort}`;
                })()}
              </Typography>
              <Typography
                component="p"
                sx={{
                  m: 0,
                  fontSize: teboxDesignSystem.typography.caption.fontSize,
                  lineHeight: 1.35,
                  color: modalInk.muted,
                  fontFamily: modalFont,
                }}
              >
                {teeTimeInfo.courseName} · ${formatMoney(teeTimeInfo.price)}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          px: padX,
          pt: gapSection,
          pb: padYBottom,
          bgcolor: modal.panelBg,
        }}
      >
        {message && (
          <Alert
            severity="success"
            sx={{
              mb: gapSection,
              borderRadius: innerRadius,
              fontSize: teboxDesignSystem.typography.body.fontSize,
              bgcolor: 'rgba(22, 163, 74, 0.08)',
              color: modalInk.body,
              border: '1px solid rgba(22, 163, 74, 0.25)',
              '& .MuiAlert-icon': { color: 'rgb(22, 163, 74)' },
            }}
          >
            {message}
          </Alert>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: gapSection,
              borderRadius: innerRadius,
              fontSize: teboxDesignSystem.typography.body.fontSize,
              bgcolor: 'rgba(220, 38, 38, 0.06)',
              border: '1px solid rgba(220, 38, 38, 0.22)',
            }}
          >
            {error}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 0 }}>
          <Button
            type="button"
            variant="outlined"
            fullWidth
            disabled={loading}
            onClick={handleGoogleLogin}
            startIcon={
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            }
            sx={{
              flex: 1,
              borderRadius: innerRadius,
              py: 1.25,
              minHeight: 44,
              textTransform: 'none',
              fontSize: teboxDesignSystem.typography.body.fontSize,
              fontWeight: 500,
              fontFamily: modalFont,
              color: modal.socialButton.color,
              borderColor: modal.socialButton.borderColor,
              bgcolor: modal.socialButton.backgroundColor,
              WebkitTapHighlightColor: 'transparent',
              '&:hover': {
                borderColor: modal.socialButton.hoverBorderColor,
                bgcolor: modal.socialButton.backgroundColor,
              },
            }}
          >
            {activeTab === 0 ? 'Log in with Google' : 'Sign up with Google'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            fullWidth
            disabled={loading}
            onClick={handleAppleLogin}
            startIcon={
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                <path fill="currentColor" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
            }
            sx={{
              flex: 1,
              borderRadius: innerRadius,
              py: 1.25,
              minHeight: 44,
              textTransform: 'none',
              fontSize: teboxDesignSystem.typography.body.fontSize,
              fontWeight: 500,
              fontFamily: modalFont,
              color: modal.socialButton.color,
              borderColor: modal.socialButton.borderColor,
              bgcolor: modal.socialButton.backgroundColor,
              WebkitTapHighlightColor: 'transparent',
              '&:hover': {
                borderColor: modal.socialButton.hoverBorderColor,
                bgcolor: modal.socialButton.backgroundColor,
              },
              '& .MuiButton-startIcon': { color: modalInk.body },
            }}
          >
            {activeTab === 0 ? 'Log in with Apple' : 'Sign up with Apple'}
          </Button>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mt: gapSection,
            mb: gapSection,
          }}
        >
          <Box sx={{ flex: 1, height: '1px', bgcolor: hairline }} />
          <Typography
            sx={{
              fontSize: teboxDesignSystem.typography.caption.fontSize,
              fontWeight: 500,
              letterSpacing: '0.2em',
              color: modalInk.muted,
              fontFamily: modalFont,
            }}
          >
            or
          </Typography>
          <Box sx={{ flex: 1, height: '1px', bgcolor: hairline }} />
        </Box>

        {activeTab === 0 && (
          <Stack
            component="form"
            spacing={gapFields}
            onSubmit={(e) => {
              e.preventDefault();
              void handleLogin();
            }}
          >
            <Box>
              <Typography component="label" htmlFor="login-email" sx={fieldLabelSx}>
                Email address
              </Typography>
              <TextField
                id="login-email"
                type="email"
                hiddenLabel
                placeholder="you@example.com"
                value={loginForm.email}
                onChange={(e) => setLoginForm((prev) => ({ ...prev, email: e.target.value }))}
                size="medium"
                fullWidth
                required
                autoComplete="email"
                sx={inputSx}
              />
            </Box>
            <Box>
              <Typography component="label" htmlFor="login-password" sx={fieldLabelSx}>
                Password
              </Typography>
              <TextField
                id="login-password"
                type={showLoginPassword ? 'text' : 'password'}
                hiddenLabel
                value={loginForm.password}
                onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e.target.value }))}
                size="medium"
                fullWidth
                required
                autoComplete="current-password"
                sx={inputSx}
                InputProps={{
                  endAdornment: passwordAdornment(showLoginPassword, () =>
                    setShowLoginPassword((v) => !v),
                  ),
                }}
              />
            </Box>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disableElevation
              disabled={loading}
              sx={primaryCtaSx}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Login'}
            </Button>
          </Stack>
        )}

        {activeTab === 1 && (
          <Stack
            component="form"
            spacing={gapFields}
            onSubmit={(e) => {
              e.preventDefault();
              void handleRegister();
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={gapFields}>
              <Box sx={{ flex: 1 }}>
                <Typography component="label" htmlFor="reg-first" sx={fieldLabelSx}>
                  First name
                </Typography>
                <TextField
                  id="reg-first"
                  hiddenLabel
                  value={registerForm.firstName}
                  onChange={(e) => setRegisterForm((prev) => ({ ...prev, firstName: e.target.value }))}
                  size="medium"
                  fullWidth
                  required
                  autoComplete="given-name"
                  sx={inputSx}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography component="label" htmlFor="reg-last" sx={fieldLabelSx}>
                  Last name
                </Typography>
                <TextField
                  id="reg-last"
                  hiddenLabel
                  value={registerForm.lastName}
                  onChange={(e) => setRegisterForm((prev) => ({ ...prev, lastName: e.target.value }))}
                  size="medium"
                  fullWidth
                  required
                  autoComplete="family-name"
                  sx={inputSx}
                />
              </Box>
            </Stack>

            <Box>
              <Typography component="label" htmlFor="reg-email" sx={fieldLabelSx}>
                Email address
              </Typography>
              <TextField
                id="reg-email"
                type="email"
                hiddenLabel
                placeholder="you@example.com"
                value={registerForm.email}
                onChange={(e) => setRegisterForm((prev) => ({ ...prev, email: e.target.value }))}
                size="medium"
                fullWidth
                required
                autoComplete="email"
                sx={inputSx}
              />
            </Box>

            <Box>
              <Typography component="label" htmlFor="reg-phone" sx={fieldLabelSx}>
                Phone number
              </Typography>
              <TextField
                id="reg-phone"
                type="tel"
                hiddenLabel
                placeholder="(555) 123-4567"
                value={registerForm.phone}
                onChange={(e) => setRegisterForm((prev) => ({ ...prev, phone: e.target.value }))}
                size="medium"
                fullWidth
                required
                autoComplete="tel"
                sx={inputSx}
              />
            </Box>

            <Box>
              <Typography component="label" htmlFor="reg-password" sx={fieldLabelSx}>
                Password
              </Typography>
              <TextField
                id="reg-password"
                type={showRegisterPassword ? 'text' : 'password'}
                hiddenLabel
                value={registerForm.password}
                onChange={(e) => setRegisterForm((prev) => ({ ...prev, password: e.target.value }))}
                size="medium"
                fullWidth
                required
                autoComplete="new-password"
                sx={inputSx}
                InputProps={{
                  endAdornment: passwordAdornment(showRegisterPassword, () =>
                    setShowRegisterPassword((v) => !v),
                  ),
                }}
              />
            </Box>

            <Box>
              <Typography component="label" htmlFor="reg-confirm" sx={fieldLabelSx}>
                Confirm password
              </Typography>
              <TextField
                id="reg-confirm"
                type={showRegisterConfirmPassword ? 'text' : 'password'}
                hiddenLabel
                value={registerForm.confirmPassword}
                onChange={(e) =>
                  setRegisterForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
                }
                size="medium"
                fullWidth
                required
                autoComplete="new-password"
                sx={inputSx}
                InputProps={{
                  endAdornment: passwordAdornment(showRegisterConfirmPassword, () =>
                    setShowRegisterConfirmPassword((v) => !v),
                  ),
                }}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disableElevation
              disabled={loading}
              sx={primaryCtaSx}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Create account'}
            </Button>
          </Stack>
        )}

        <Box sx={{ textAlign: 'left', mt: gapSection + 0.5, lineHeight: 1.5 }}>
          <Typography
            component="span"
            sx={{
              fontSize: teboxDesignSystem.typography.body.fontSize,
              color: modalInk.muted,
              fontFamily: modalFont,
            }}
          >
            {activeTab === 0 ? "Don't have an account? " : 'Already have an account? '}
          </Typography>
          <Button
            type="button"
            variant="text"
            onClick={() => setTab(activeTab === 0 ? 1 : 0)}
            sx={{
              p: 0,
              minWidth: 0,
              display: 'inline',
              fontSize: teboxDesignSystem.typography.body.fontSize,
              fontWeight: 500,
              fontFamily: modalFont,
              color: accent,
              textTransform: 'none',
              verticalAlign: 'baseline',
              '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
            }}
          >
            {activeTab === 0 ? 'Sign up' : 'Log in'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LoginRegisterModal;
